import React, { useState, useEffect } from "react";
import { Table, message, Dropdown, Tag } from "antd";
import { EyeOutlined, EditOutlined, EnvironmentOutlined, DeleteOutlined, FileTextOutlined, DownloadOutlined, PrinterOutlined, DownOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as customerService from "../../../../Customers/services/customerService";
import * as accountSettingsService from "../../../../../services/accountSettingsService";
import { generateInvoicePDF } from "../../../../Reports/services/invoiceGenerator";
import { useAuth } from "../../../../../AuthContext";
import JobTableColumns from "./JobTableColumns";
import JobEditModal from "./JobEditModal";
import JobDeleteConfirm from "./JobDeleteConfirm";
import ReceiptPreview from "../../../../../components/ReceiptPreview";
import { getAppliedDiscount } from "../../../../../utils/pricingTiers";

const JobTable = ({
  jobs,
  onViewDetails,
  onEdit,
  onDelete,
  onCreateInvoice,
  showDaysSinceOrder = false,
  getDaysSinceOrder = () => null,
  getUrgencyColor = () => "default",
}) => {
  const navigate = useNavigate();
  const { token } = useAuth();

  // Fetch account settings for invoice generation
  const { data: accountSettings } = useQuery({
    queryKey: ["accountSettings", token],
    queryFn: () => accountSettingsService.getAccountSettings(),
    enabled: !!token,
  });
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState({});
  const [jobToDelete, setJobToDelete] = useState(null);
  const [receiptJob, setReceiptJob] = useState(null);

  // Fetch customers when component mounts
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await customerService.getCustomersList();
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Failed to fetch customers:", error);
      }
    };

    fetchCustomers();
  }, []);

  const handleEdit = (job) => {
    setEditingId(job.id);
    setEditFormData({
      customerId: job.customer?.id || "",
      title: job.title || "",
      description: job.description || "",
      workSiteAddress: job.workSiteAddress || "",
    });
    // Pre-fill the customer search with the current customer's name
    setCustomerSearchTerm(job.customer?.name || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
    setShowCustomerDropdown({});
  };

  const handleSaveEdit = async (jobId) => {
    try {
      await onEdit({ ...editFormData, id: jobId });
      setEditingId(null);
      setEditFormData({});
      setShowCustomerDropdown({});
    } catch (error) {
      console.error("Failed to update job:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  const handleCustomerSearch = (jobId, searchTerm) => {
    if (searchTerm.trim() === "") {
      setFilteredCustomers([]);
    } else {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  };

  const selectCustomer = (jobId, customer) => {
    setEditFormData(prev => ({
      ...prev,
      customerId: customer.id
    }));
    // Update the search term to show the selected customer's name
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(prev => ({ ...prev, [jobId]: false }));
  };

  // Helper functions for mobile view - computeTotalCost must come first
  // Uses getAppliedDiscount to respect job-level discount overrides
  const computeTotalCost = (job) => {
    try {
      if (!job) return 0;

      // Calculate product cost from job.sales
      let productCost = 0;
      let totalQuantity = 0;
      if (job.sales && Array.isArray(job.sales)) {
        job.sales.forEach((sale) => {
          if (sale.saleItems && Array.isArray(sale.saleItems)) {
            sale.saleItems.forEach((saleItem) => {
              const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
              const quantity = parseInt(saleItem.quantity) || 1;
              productCost += unitPrice * quantity;
              totalQuantity += quantity;
            });
          }
        });
      }

      // Get applied discount using utility (respects job-level overrides)
      const appliedDiscount = getAppliedDiscount(job, accountSettings, totalQuantity);
      const discountPercent = appliedDiscount.discount;

      const discountAmount = productCost * (discountPercent / 100);
      const subtotalAfterDiscount = productCost - discountAmount;
      const jobTaxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
      const taxAmount = job.includeTax ? subtotalAfterDiscount * jobTaxRate : 0;
      return subtotalAfterDiscount + taxAmount;
    } catch (e) {
      return 0;
    }
  };

  const calculatePaymentStatus = (job) => {
    try {
      if (!job) return "UNPAID";
      const totalCost = computeTotalCost(job);
      const totalPaid = Array.isArray(job.payments) && job.payments.length > 0
        ? job.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
        : 0;
      if (totalPaid === 0) return "UNPAID";
      else if (totalPaid >= totalCost) return "PAID";
      else return "PARTIALLY_PAID";
    } catch (e) {
      return "UNPAID";
    }
  };

  const openMapNavigation = (address) => {
    if (!address) {
      alert("No work site address available for this job.");
      return;
    }
    const encodedAddress = encodeURIComponent(address);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const link = document.createElement('a');
    link.href = isIOS
      ? `https://maps.apple.com/?daddr=${encodedAddress}`
      : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle creating invoice from a wholesale order - generates PDF directly
  const handleCreateInvoice = async (job) => {
    if (onCreateInvoice) {
      onCreateInvoice(job);
    } else {
      try {
        message.loading({ content: "Generating invoice...", key: "invoice" });
        const result = await generateInvoicePDF(job, accountSettings);
        if (result && Array.isArray(result.missingFields) && result.missingFields.length > 0) {
          message.warning({
            content: `Invoice generated, but the customer is missing: ${result.missingFields.join(", ")}.`,
            key: "invoice",
            duration: 6,
          });
        } else {
          message.success({ content: "Invoice generated successfully!", key: "invoice" });
        }
      } catch (error) {
        console.error("Error generating invoice:", error);
        message.error({ content: "Failed to generate invoice", key: "invoice" });
      }
    }
  };

  // Convert job to invoice format for receipt
  const jobToInvoice = (job) => {
    let productCost = 0;
    let totalQuantity = 0;
    const products = [];

    if (job.sales && Array.isArray(job.sales)) {
      job.sales.forEach((sale) => {
        if (sale.saleItems && Array.isArray(sale.saleItems)) {
          sale.saleItems.forEach((saleItem) => {
            const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
            const quantity = parseInt(saleItem.quantity) || 1;
            const lineTotal = unitPrice * quantity;
            productCost += lineTotal;
            totalQuantity += quantity;
            products.push({
              name: saleItem.product?.name || 'Unknown Product',
              quantity,
              unitPrice,
              lineTotal
            });
          });
        }
      });
    }

    const appliedDiscount = getAppliedDiscount(job, accountSettings, totalQuantity);
    const discountPercent = appliedDiscount.discount;
    const discountLabel = appliedDiscount.label ? `${appliedDiscount.label} (${discountPercent}%)` : "";
    const discountAmount = productCost * (discountPercent / 100);
    const subtotalAfterDiscount = productCost - discountAmount;
    const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
    const taxAmount = job.includeTax ? subtotalAfterDiscount * taxRate : 0;
    const totalCost = subtotalAfterDiscount + taxAmount;
    const totalPaid = Array.isArray(job.payments) && job.payments.length > 0
      ? job.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
      : 0;
    const balanceDue = totalCost - totalPaid;

    return {
      id: job.id,
      job: job,
      customerName: job.customer?.name || job.customerName || "Unknown Customer",
      date: job.startDate || job.createdAt,
      title: job.title,
      products,
      productCost,
      totalQuantity,
      discountPercent,
      discountLabel,
      discountAmount,
      subtotalAfterDiscount,
      taxAmount,
      totalCost,
      totalPaid,
      balanceDue,
    };
  };

  // Handle thermal receipt
  const handleOpenReceipt = (job) => {
    setReceiptJob(job);
  };

  // Get invoice dropdown menu items
  const getInvoiceMenuItems = (job) => [
    {
      key: 'pdf',
      label: 'Standard Invoice (PDF)',
      icon: <DownloadOutlined />,
      onClick: () => handleCreateInvoice(job),
    },
    {
      key: 'receipt',
      label: 'Thermal Receipt (58mm)',
      icon: <PrinterOutlined />,
      onClick: () => handleOpenReceipt(job),
    },
  ];

  const columns = JobTableColumns({
    editingId,
    onViewDetails,
    onEdit: handleEdit,
    onDelete: setJobToDelete,
    onSaveEdit: handleSaveEdit,
    onCancelEdit: handleCancelEdit,
    onCreateInvoice: handleCreateInvoice,
    onOpenReceipt: handleOpenReceipt,
    accountSettings,
    showDaysSinceOrder,
    getDaysSinceOrder,
    getUrgencyColor,
  });

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
          defaultSortOrder="descend"
          sortDirections={['descend', 'ascend']}
          className="responsive-table"
        />
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {jobs && jobs.length > 0 ? (
          jobs.map((job) => {
            const paymentStatus = calculatePaymentStatus(job);
            const totalCost = computeTotalCost(job);
            const customerName = job.customerName || job.customer?.name || job.customer?.username || "Unknown Customer";

            return (
              <div
                key={job.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                {/* Header with ID, Status Tags */}
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-xs text-gray-500">Job #{job.id}</div>
                      <div className="text-lg font-semibold text-gray-900 mt-1">{job.title}</div>
                    </div>
                    <div className="text-right text-lg font-bold text-gray-900">
                      ${totalCost.toFixed(2)}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Job Status */}
                    <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                      job.status === "IN_PROGRESS"
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {job.status === "IN_PROGRESS" ? "In Progress" : "Completed"}
                    </span>
                    {/* Payment Status */}
                    <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                      paymentStatus === "PAID"
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : paymentStatus === "PARTIALLY_PAID"
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {paymentStatus === "PAID" ? "Paid" : paymentStatus === "PARTIALLY_PAID" ? "Partially Paid" : "Unpaid"}
                    </span>
                  </div>
                </div>

                {/* Customer */}
                <div className="mb-3">
                  <div className="text-xs text-gray-600 mb-1">Customer</div>
                  <div className="text-sm font-medium text-gray-900">{customerName}</div>
                </div>

                {/* Invoice Date and Days Since */}
                <div className={`grid ${showDaysSinceOrder ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-3`}>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Invoice Date</div>
                    <div className="text-sm text-gray-900">
                      {job.startDate ? new Date(job.startDate).toLocaleDateString() : "N/A"}
                    </div>
                  </div>
                  {showDaysSinceOrder && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Days Since</div>
                      <div className="text-sm">
                        {(() => {
                          const days = getDaysSinceOrder(job.startDate || job.orderDate);
                          if (days === null) return <span className="text-gray-400">N/A</span>;
                          const color = getUrgencyColor(days);
                          return (
                            <Tag color={color} icon={<ClockCircleOutlined />} style={{ margin: 0 }}>
                              {days} day{days !== 1 ? 's' : ''}
                            </Tag>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons - Two rows for better mobile layout */}
                <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                  {/* First row: View, Edit, Invoice */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => onViewDetails(job)}
                      className="flex flex-col items-center justify-center py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <EyeOutlined className="text-lg mb-1" />
                      <span className="text-xs font-medium">View</span>
                    </button>
                    <button
                      onClick={() => handleEdit(job)}
                      className="flex flex-col items-center justify-center py-2 bg-yellow-50 text-yellow-600 rounded-md hover:bg-yellow-100 transition-colors"
                    >
                      <EditOutlined className="text-lg mb-1" />
                      <span className="text-xs font-medium">Edit</span>
                    </button>
                    <Dropdown
                      menu={{ items: getInvoiceMenuItems(job) }}
                      trigger={['click']}
                    >
                      <button
                        className="flex flex-col items-center justify-center py-2 bg-purple-50 text-purple-600 rounded-md hover:bg-purple-100 transition-colors"
                      >
                        <FileTextOutlined className="text-lg mb-1" />
                        <span className="text-xs font-medium">Invoice <DownOutlined style={{ fontSize: 8 }} /></span>
                      </button>
                    </Dropdown>
                  </div>
                  {/* Second row: Map (if available), Delete */}
                  <div className="grid grid-cols-2 gap-2">
                    {job.workSiteAddress && (
                      <button
                        onClick={() => openMapNavigation(job.workSiteAddress)}
                        className="flex flex-col items-center justify-center py-2 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors"
                      >
                        <EnvironmentOutlined className="text-lg mb-1" />
                        <span className="text-xs font-medium">Map</span>
                      </button>
                    )}
                    <button
                      onClick={() => setJobToDelete(job)}
                      className={`flex flex-col items-center justify-center py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors ${!job.workSiteAddress ? 'col-span-2' : ''}`}
                    >
                      <DeleteOutlined className="text-lg mb-1" />
                      <span className="text-xs font-medium">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-500">No jobs found</div>
        )}
      </div>

      <JobDeleteConfirm
        job={jobToDelete}
        onCancel={() => setJobToDelete(null)}
        onConfirm={(id) => {
          onDelete(id);
          setJobToDelete(null);
        }}
      />

      <JobEditModal
        isOpen={!!editingId}
        editingId={editingId}
        editFormData={editFormData}
        customers={customers}
        filteredCustomers={filteredCustomers}
        customerSearchTerm={customerSearchTerm}
        showCustomerDropdown={showCustomerDropdown[editingId]}
        onClose={handleCancelEdit}
        onInputChange={handleInputChange}
        onCustomerSearch={handleCustomerSearch}
        onCustomerSelect={selectCustomer}
        onSaveEdit={handleSaveEdit}
        onDropdownToggle={(show) => setShowCustomerDropdown(prev => ({ ...prev, [editingId]: show }))}
      />

      {/* Receipt Preview Modal */}
      <ReceiptPreview
        open={!!receiptJob}
        onClose={() => setReceiptJob(null)}
        invoice={receiptJob ? jobToInvoice(receiptJob) : null}
        accountSettings={accountSettings}
      />
    </>
  );
};

export default JobTable;
